// SMS Code-Based Password Reset - Adapted from your working mobile verification
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import coocLogo from '../../assets/cooc-logo-black.png';
import { updatePassword, signInWithEmailAndPassword, sendPasswordResetEmail } from 'firebase/auth';
import { getFirestore, collection, query, where, getDocs, addDoc, doc, getDoc } from 'firebase/firestore';
import { auth } from '../../firebase';
import './Auth.css';

const ForgotPassword = () => {
  const [step, setStep] = useState(1); // 1: verify details, 2: SMS code, 3: new password
  const [email, setEmail] = useState('');
  const [mobile, setMobile] = useState('');
  const [dateOfBirth, setDateOfBirth] = useState('');
  const [smsCode, setSmsCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [personalEmail, setPersonalEmail] = useState('');
  const [verificationAttempts, setVerificationAttempts] = useState(0);

  // Initialize Firebase phone auth components
  useEffect(() => {
    // Load Firebase compat for phone auth
    if (typeof window !== 'undefined' && window.firebase) {
      console.log('Firebase compat available for phone auth');
    }
  }, []);

  // Step 1: Verify account details and send SMS
  const handleVerifyDetails = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const db = getFirestore();
      
      // 18.09.25 13:30 - Verify account details (same as before)
      const accountInfoRef = doc(db, 'Users', email);
      const accountSnapshot = await getDoc(accountInfoRef);

      if (!accountSnapshot.exists()) {
        setError('Account information not found. Please contact accounts@cooc.com.au');
        setLoading(false);
        return;
      }

      const accountData = accountSnapshot.data();

      // Verify mobile and DOB match
      if (accountData.mobile !== mobile) {
        setError('Mobile number does not match our records for this COOC email.');
        setLoading(false);
        return;
      }

      if (accountData.dateOfBirth !== dateOfBirth) {
        setError('Date of birth does not match our records for this COOC email.');
        setLoading(false);
        return;
      }

      setPersonalEmail(accountData.personalEmail || 'Not provided');

      // 18.09.25 13:30 - Send SMS code using your working Firebase pattern
      await sendSMSCode();

    } catch (error) {
      console.error('Account verification error:', error);
      setError('Error verifying account details. Please try again.');
      setLoading(false);
    }
  };

  // Send SMS code using your existing working pattern
  const sendSMSCode = async () => {
    try {
      console.log('18.09.25 13:30 - Sending SMS code using working Firebase pattern');
      
      // Format Australian phone number (same as your mobile verification)
      let formattedPhone = mobile;
      if (!formattedPhone.startsWith('+61')) {
        formattedPhone = '+61' + formattedPhone.replace(/^0/, '');
      }

      // Create reCAPTCHA container dynamically
      const recaptchaContainer = document.createElement('div');
      recaptchaContainer.id = 'recaptcha-container-reset';
      recaptchaContainer.style.margin = '20px auto';
      recaptchaContainer.style.textAlign = 'center';
      
      // Find form container and add reCAPTCHA
      const formContainer = document.querySelector('.auth-form');
      if (formContainer) {
        formContainer.appendChild(recaptchaContainer);
      }

      // Initialize reCAPTCHA verifier (same pattern as your working version)
      if (window.firebase && !window.resetRecaptchaVerifier) {
        window.resetRecaptchaVerifier = new window.firebase.auth.RecaptchaVerifier('recaptcha-container-reset', {
          'size': 'normal',
          'callback': (response) => {
            console.log('18.09.25 13:30 - reCAPTCHA solved for password reset');
          }
        });
      }

      // Send SMS using Firebase phone auth (same as your working code)
      if (window.firebase) {
        window.resetConfirmationResult = await window.firebase.auth().signInWithPhoneNumber(
          formattedPhone, 
          window.resetRecaptchaVerifier
        );

        console.log('18.09.25 13:30 - SMS code sent successfully');
        
        // Move to step 2
        setStep(2);
        setLoading(false);

        // Log security event
        await addDoc(collection(getFirestore(), 'Users', email, 'SecurityEvents'), {
          type: 'sms_password_reset_code_sent',
          coocEmail: email,
          mobile: mobile,
          timestamp: new Date(),
          userAgent: navigator.userAgent,
          verificationMethod: 'sms_code'
        });

      } else {
        throw new Error('Firebase compat not available');
      }

    } catch (error) {
      console.error('SMS sending error:', error);
      setError('Error sending SMS: ' + error.message);
      setLoading(false);
    }
  };

  // Step 2: Verify SMS code (same pattern as your working verification)
  const handleVerifySMSCode = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      if (window.resetConfirmationResult && smsCode.length === 6) {
        // Verify SMS code using Firebase (same as your working pattern)
        const result = await window.resetConfirmationResult.confirm(smsCode);
        console.log('18.09.25 13:35 - SMS code verified successfully');

        // Move to password reset step
        setStep(3);
        setLoading(false);

      } else {
        setError('Please enter a valid 6-digit code.');
        setLoading(false);
      }

    } catch (error) {
      console.error('SMS code verification error:', error);
      setLoading(false);
      
      setVerificationAttempts(prev => prev + 1);
      if (verificationAttempts >= 2) {
        setError('Too many incorrect attempts. Please restart the process.');
        setTimeout(() => {
          setStep(1);
          setSmsCode('');
          setVerificationAttempts(0);
        }, 3000);
      } else {
        setError(`Invalid code. ${3 - verificationAttempts - 1} attempts remaining.`);
        setSmsCode('');
      }
    }
  };

  // Step 3: Update password using Firebase reset
  const handleUpdatePassword = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    if (newPassword !== confirmPassword) {
      setError('Passwords do not match.');
      setLoading(false);
      return;
    }

    if (newPassword.length < 8) {
      setError('Password must be at least 8 characters long.');
      setLoading(false);
      return;
    }

    try {
      // Since SMS verification is complete, send standard Firebase reset email
      // This will work because the user is now verified
      await sendPasswordResetEmail(auth, email);
      
      // Show success message
      setStep(4);
      setLoading(false);

    } catch (error) {
      console.error('Password reset error:', error);
      setError('Error processing password reset. Please try again.');
      setLoading(false);
    }
  };

  const handleStartOver = () => {
    setStep(1);
    setEmail('');
    setMobile('');
    setDateOfBirth('');
    setSmsCode('');
    setNewPassword('');
    setConfirmPassword('');
    setError('');
    setVerificationAttempts(0);
  };

  // Validate SMS code input (same as your working version)
  const handleSMSCodeChange = (e) => {
    const value = e.target.value.replace(/\D/g, ''); // Only digits
    setSmsCode(value.slice(0, 6)); // Max 6 digits
  };

  return (
    <div className="auth-container">
      <div className="auth-card">
        <div className="auth-header">
          <div className="cooc-logo" style={{ backgroundImage: `url(${coocLogo})` }}></div>
          <h1>Reset Your Password</h1>
          <p>Secure password recovery for COOC users</p>
          <div className="progress-bar">
            <div className="progress-fill" style={{width: `${(step / 4) * 100}%`}}></div>
          </div>
        </div>

        {step === 1 && (
          // Step 1: Account verification
          <form onSubmit={handleVerifyDetails} className="auth-form">
            <div className="form-group">
              <label htmlFor="email">Your COOC Email Address</label>
              <input
                type="email"
                id="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="your.name@cooc.com.au"
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="mobile">Your Mobile Number</label>
              <input
                type="tel"
                id="mobile"
                value={mobile}
                onChange={(e) => setMobile(e.target.value)}
                placeholder="0412345678"
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="dateOfBirth">Your Date of Birth</label>
              <input
                type="date"
                id="dateOfBirth"
                value={dateOfBirth}
                onChange={(e) => setDateOfBirth(e.target.value)}
                required
              />
            </div>

            {error && <div className="error-message">{error}</div>}

            <button type="submit" disabled={loading} className="auth-button">
              {loading ? 'Verifying & Sending...' : 'Send SMS Code'}
            </button>

            <div className="security-info">
              <p><strong>Next:</strong> We'll send a 6-digit verification code to your mobile.</p>
            </div>
          </form>
        )}

        {step === 2 && (
          // Step 2: SMS code entry (same design as your working version)
          <form onSubmit={handleVerifySMSCode} className="auth-form">
            <div className="success-info" style={{background: '#e8f0fe', padding: '16px', borderRadius: '8px', marginBottom: '24px'}}>
              <p>✅ Account verified: <strong>{email}</strong></p>
              <p>📱 Verification code sent to: <strong>{mobile}</strong></p>
            </div>

            <div className="form-group">
              <label htmlFor="smsCode">Enter 6-Digit Code</label>
              <input
                type="text"
                id="smsCode"
                value={smsCode}
                onChange={handleSMSCodeChange}
                placeholder="123456"
                maxLength="6"
                required
                style={{
                  textAlign: 'center', 
                  fontSize: '24px', 
                  letterSpacing: '8px',
                  fontWeight: '600'
                }}
              />
              <small>Enter the code sent to your mobile phone</small>
            </div>

            {error && <div className="error-message">{error}</div>}

            <button type="submit" disabled={loading || smsCode.length !== 6} className="auth-button">
              {loading ? 'Verifying Code...' : 'Verify Code'}
            </button>

            <p style={{fontSize: '13px', color: '#666', marginTop: '16px', textAlign: 'center'}}>
              Didn't receive the code? 
              <button type="button" onClick={sendSMSCode} style={{background: 'none', border: 'none', color: '#1a73e8', cursor: 'pointer', textDecoration: 'underline'}}>
                Resend SMS
              </button>
            </p>
          </form>
        )}

        {step === 3 && (
          // Step 3: Password reset completion
          <div className="auth-form">
            <div className="success-info" style={{background: '#d4edda', padding: '16px', borderRadius: '8px', marginBottom: '24px'}}>
              <p>✅ Mobile verified successfully!</p>
              <p>📧 Password reset link will be sent to your COOC email</p>
            </div>

            <button onClick={handleUpdatePassword} disabled={loading} className="auth-button">
              {loading ? 'Sending Reset Link...' : 'Send Password Reset Email'}
            </button>
          </div>
        )}

        {step === 4 && (
          // Step 4: Success
          <div className="success-step">
            <div className="success-icon">✅</div>
            <h3>Password Reset Email Sent!</h3>
            <div className="success-info">
              <p>We've verified your identity and sent a password reset link to:</p>
              <p className="email-sent">{email}</p>
              <p>Check your email and follow the instructions to reset your password.</p>
            </div>

            <div className="form-actions">
              <button onClick={handleStartOver} className="secondary-button">
                Reset Another Account
              </button>
              <a href="/cooc-login.html" className="auth-button">
                Back to Sign In
              </a>
            </div>
          </div>
        )}

        <div className="auth-footer">
          <p>Remember your password? <a href="/cooc-login.html">Sign In</a></p>
          <p>Need help? Contact <strong>accounts@cooc.com.au</strong></p>
        </div>
      </div>
    </div>
  );
};

export default ForgotPassword;